import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../contexts/TestAuthContext';
import { AlertTriangle, CheckCircle2, ChevronDown, ChevronUp, ExternalLink, X, Check, MessageSquare, Sparkles, Loader2 } from 'lucide-react';
import api from '../../utils/api';
import { wsManager, JobUpdate } from '../../utils/websocket';

interface Anomaly {
  id: string;
  anomaly_type: string;
  severity: 'low' | 'medium' | 'high' | 'critical';
  title: string;
  description: string;
  explanation: string;
  affected_period?: string;
  affected_vendor?: string;
  related_amount?: number;
  deviation_percent?: number;
  transaction_id?: string;
  transaction_ids?: string[];
  is_resolved: boolean;
  resolution_note?: string;
  resolved_at?: string;
  // AI Analysis fields
  ai_analysis?: {
    explanation?: string;
    likely_false_positive?: boolean;
    recommendation?: string;
    confidence?: number;
  };
  likely_false_positive?: boolean;
  ai_recommendation?: string;
}

interface ResolvedAnomaly {
  id: string;
  note: string;
  resolved_at: string;
  anomaly_type?: string;
  deviation_percent?: number;
  related_amount?: number;
}

interface AnomalyBannerProps {
  canonicalData: {
    periods?: Array<{
      year: number;
      month: number;
      transactions: any[];
    }>;
  } | null;
  businessContext?: {
    business_type?: string;
    industry?: string;
    event_type?: string;
    event_start?: string;
    event_end?: string;
  };
  enableAIAnalysis?: boolean;
  onViewTransaction?: (transactionId: string) => void;
  resolvedAnomalies?: ResolvedAnomaly[];
  onResolveAnomaly?: (anomalyId: string, note: string) => void;
  onUnresolveAnomaly?: (anomalyId: string) => void;
  // Cached analysis from wizard data
  cachedAnalysis?: {
    anomalies: Anomaly[];
    dataHash: string;
    analyzedAt: string;
  } | null;
  onAnalysisComplete?: (analysis: { anomalies: Anomaly[]; dataHash: string; analyzedAt: string }) => void;
}

// Simple hash function to detect data changes
function hashData(data: any): string {
  const str = JSON.stringify(data);
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash;
  }
  return hash.toString(36);
}

export function AnomalyBanner({ 
  canonicalData, 
  businessContext,
  enableAIAnalysis = false,
  onViewTransaction,
  resolvedAnomalies = [],
  onResolveAnomaly,
  onUnresolveAnomaly,
  cachedAnalysis,
  onAnalysisComplete
}: AnomalyBannerProps) {
  const [anomalies, setAnomalies] = useState<Anomaly[]>([]);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const [showResolved, setShowResolved] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [aiAnalyzedCount, setAiAnalyzedCount] = useState(0);
  const [thresholdsUsed, setThresholdsUsed] = useState<Record<string, number> | null>(null);
  const [usingCachedAnalysis, setUsingCachedAnalysis] = useState(false);
  
  // AI Analysis job polling state
  const [aiJobId, setAiJobId] = useState<string | null>(null);
  const [aiJobProgress, setAiJobProgress] = useState<{ processed: number; total: number; status: string } | null>(null);
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  
  // Resolve modal state
  const [resolveModalOpen, setResolveModalOpen] = useState(false);
  const [resolvingAnomalyId, setResolvingAnomalyId] = useState<string | null>(null);
  const [resolveNote, setResolveNote] = useState('');

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const { getToken } = useAuth();

  useEffect(() => {
    if (canonicalData?.periods && canonicalData.periods.length > 0) {
      const currentHash = hashData(canonicalData.periods);
      
      // Check if we have valid cached analysis for this data
      if (cachedAnalysis && cachedAnalysis.dataHash === currentHash && cachedAnalysis.anomalies.length > 0) {
        console.log('📦 Using cached AI anomaly analysis from:', cachedAnalysis.analyzedAt);
        
        // Merge with current resolved state
        const resolvedIds = new Set(resolvedAnomalies.map(r => r.id));
        const mergedAnomalies = cachedAnalysis.anomalies.map((a: Anomaly) => {
          const resolved = resolvedAnomalies.find(r => r.id === a.id);
          return {
            ...a,
            is_resolved: resolvedIds.has(a.id),
            resolution_note: resolved?.note,
            resolved_at: resolved?.resolved_at
          };
        });
        
        setAnomalies(mergedAnomalies);
        setAiAnalyzedCount(cachedAnalysis.anomalies.filter(a => a.ai_analysis).length);
        setUsingCachedAnalysis(true);
        return;
      }
      
      // No cache or data changed - run fresh analysis
      console.log('🔄 Running fresh AI anomaly analysis...');
      setUsingCachedAnalysis(false);
      detectAnomalies(currentHash);
    }
  }, [canonicalData, cachedAnalysis]);

  const detectAnomalies = async (dataHash?: string) => {
    if (!canonicalData?.periods) return;
    
    setLoading(true);
    setError(null);
    setAiJobId(null);
    setAiJobProgress(null);

    try {
      // Prepare resolved anomalies with metadata for adaptive learning
      const resolvedWithMetadata = resolvedAnomalies.map(r => ({
        id: r.id,
        anomaly_type: r.anomaly_type,
        deviation_percent: r.deviation_percent,
        related_amount: r.related_amount
      }));
      
      const token = await getToken();
      
      // Use the new cached endpoint for better performance
      const data = await api.post('/api/v1/anomalies/canonical/with-cache', { 
        periods: canonicalData.periods,
        business_type: businessContext?.business_type,
        business_context: businessContext,
        resolved_anomalies: resolvedWithMetadata,
        enable_ai_analysis: enableAIAnalysis
      }, {
        headers: { 'Authorization': `Bearer ${token}` },
        showErrorToast: false
      });
      
      // Store thresholds for display
      if (data.thresholds_used) setThresholdsUsed(data.thresholds_used);
      
      // Check if data came from cache
      if (data.from_cache) {
        console.log('📦 Using cached anomaly analysis (hash:', data.cache_hash, ')');
        setUsingCachedAnalysis(true);
      }
      
      // Merge with resolved state
      const resolvedIds = new Set(resolvedAnomalies.map(r => r.id));
      const mergedAnomalies = (data.anomalies || []).map((a: Anomaly) => {
        const resolved = resolvedAnomalies.find(r => r.id === a.id);
        return {
          ...a,
          is_resolved: resolvedIds.has(a.id),
          resolution_note: resolved?.note,
          resolved_at: resolved?.resolved_at
        };
      });
      
      setAnomalies(mergedAnomalies);
      setLoading(false);
      
      // If AI analysis job was queued, start polling
      if (data.ai_analysis_job_id) {
        console.log('🤖 AI analysis job queued:', data.ai_analysis_job_id);
        setAiJobId(data.ai_analysis_job_id);
        startPollingAiJob(data.ai_analysis_job_id, dataHash);
      }
      
      // If no AI job and we have anomalies, save to cache
      if (!data.ai_analysis_job_id && onAnalysisComplete && dataHash && mergedAnomalies.length > 0) {
        console.log('💾 Saving anomaly analysis to wizard data...');
        onAnalysisComplete({
          anomalies: data.anomalies,
          dataHash: dataHash,
          analyzedAt: new Date().toISOString()
        });
      }
    } catch (err: any) {
      console.error('Anomaly detection error:', err);
      setError(err.message);
      setLoading(false);
    }
  };
  
  // Handle AI job updates (from WebSocket or polling)
  const handleAiJobUpdate = (update: JobUpdate, dataHash?: string) => {
    // Update progress
    setAiJobProgress({
      processed: update.analyzed || update.processed || 0,
      total: update.total || 0,
      status: update.status || 'processing'
    });
    
    // Check if complete
    if (update.type === 'complete' || update.status === 'completed') {
      console.log('✅ AI analysis complete');
      
      // Merge AI results with existing anomalies
      if (update.results && Array.isArray(update.results)) {
        setAnomalies(prev => {
          const aiResultsById = new Map(update.results!.map((r: any) => [r.id, r]));
          return prev.map(a => {
            const aiResult = aiResultsById.get(a.id);
            if (aiResult?.ai_analysis) {
              return {
                ...a,
                ai_analysis: aiResult.ai_analysis,
                likely_false_positive: aiResult.ai_analysis?.likely_false_positive,
                ai_recommendation: aiResult.ai_analysis?.recommendation
              };
            }
            return a;
          });
        });
        
        setAiAnalyzedCount(update.results.filter((r: any) => r.ai_analysis).length);
        
        // Save complete results to cache
        if (onAnalysisComplete && dataHash) {
          console.log('💾 Saving AI-enriched analysis to wizard data...');
          onAnalysisComplete({
            anomalies: update.results,
            dataHash: dataHash,
            analyzedAt: new Date().toISOString()
          });
        }
      }
      
      setAiJobId(null);
      setAiJobProgress(null);
    } else if (update.status === 'failed') {
      console.error('❌ AI analysis job failed:', update.error);
      setAiJobId(null);
      setAiJobProgress(null);
    }
  };
  
  // Start listening for AI job updates via WebSocket (with polling fallback)
  const startPollingAiJob = (jobId: string, dataHash?: string) => {
    // Clear any existing polling
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
    }
    
    // Try WebSocket first
    const unsubscribe = wsManager.subscribeToJob(jobId, (update) => {
      handleAiJobUpdate(update, dataHash);
      
      // Stop polling if WebSocket is working
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    });
    
    // Also start polling as fallback (WebSocket may not be available)
    const pollJob = async () => {
      try {
        const token = await getToken();
        const status = await api.get(`/api/v1/anomalies/ai-analysis/${jobId}/status`, {
          headers: { 'Authorization': `Bearer ${token}` },
          showErrorToast: false
        });
        
        if (!status.success) {
          console.log('⚠️ AI job not found or failed:', jobId);
          stopPolling();
          unsubscribe();
          return;
        }
        
        // Convert polling response to JobUpdate format
        handleAiJobUpdate({
          type: status.status === 'completed' ? 'complete' : 'progress',
          job_id: jobId,
          status: status.status,
          total: status.total,
          processed: status.processed,
          analyzed: status.analyzed,
          percent: status.percent,
          results: status.results
        }, dataHash);
        
        // If complete, stop polling
        if (status.status === 'completed' || status.status === 'failed') {
          stopPolling();
          unsubscribe();
        }
      } catch (err) {
        console.error('Error polling AI job:', err);
      }
    };
    
    // Poll immediately, then every 3 seconds (WebSocket should take over if available)
    pollJob();
    pollingIntervalRef.current = setInterval(pollJob, 3000);
  };
  
  const stopPolling = () => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
  };
  
  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      stopPolling();
    };
  }, []);

  // Update anomalies when resolvedAnomalies changes
  useEffect(() => {
    if (anomalies.length > 0) {
      const resolvedIds = new Set(resolvedAnomalies.map(r => r.id));
      setAnomalies(prev => prev.map(a => {
        const resolved = resolvedAnomalies.find(r => r.id === a.id);
        return {
          ...a,
          is_resolved: resolvedIds.has(a.id),
          resolution_note: resolved?.note,
          resolved_at: resolved?.resolved_at
        };
      }));
    }
  }, [resolvedAnomalies]);

  const handleResolve = (anomalyId: string) => {
    setResolvingAnomalyId(anomalyId);
    setResolveNote('');
    setResolveModalOpen(true);
  };

  const confirmResolve = () => {
    if (resolvingAnomalyId && onResolveAnomaly) {
      onResolveAnomaly(resolvingAnomalyId, resolveNote);
    }
    setResolveModalOpen(false);
    setResolvingAnomalyId(null);
    setResolveNote('');
  };

  const handleUnresolve = (anomalyId: string) => {
    if (onUnresolveAnomaly) {
      onUnresolveAnomaly(anomalyId);
    }
  };

  const unresolvedAnomalies = anomalies.filter(a => !a.is_resolved);
  const resolvedAnomaliesList = anomalies.filter(a => a.is_resolved);
  const highPriority = unresolvedAnomalies.filter(a => a.severity === 'critical' || a.severity === 'high');
  const displayAnomalies = showAll ? unresolvedAnomalies : unresolvedAnomalies.slice(0, 5);

  // Severity config
  const severityConfig = {
    critical: { bg: 'bg-red-100', border: 'border-red-300', text: 'text-red-800', badge: 'bg-red-200 text-red-800' },
    high: { bg: 'bg-orange-100', border: 'border-orange-300', text: 'text-orange-800', badge: 'bg-orange-200 text-orange-800' },
    medium: { bg: 'bg-yellow-100', border: 'border-yellow-300', text: 'text-yellow-800', badge: 'bg-yellow-200 text-yellow-800' },
    low: { bg: 'bg-gray-100', border: 'border-gray-300', text: 'text-gray-700', badge: 'bg-gray-200 text-gray-700' }
  };

  // Loading state
  if (loading) {
    return (
      <div className="bg-gradient-to-r from-purple-50 to-blue-50 border border-purple-200 rounded-lg p-4 mb-4" data-testid="anomaly-banner-loading">
        <div className="flex items-center gap-3">
          <div className="relative">
            <Sparkles className="w-5 h-5 text-purple-600 animate-pulse" />
          </div>
          <div>
            <span className="font-medium text-purple-900">AI Analyzing Financial Data...</span>
            <p className="text-sm text-purple-600">Detecting anomalies and generating insights</p>
          </div>
          <div className="ml-auto">
            <Loader2 className="w-5 h-5 text-purple-500 animate-spin" />
          </div>
        </div>
      </div>
    );
  }
  
  // AI Job in progress (show progress bar)
  const renderAiJobProgress = () => {
    if (!aiJobId || !aiJobProgress) return null;
    
    const progressPercent = aiJobProgress.total > 0 
      ? Math.round((aiJobProgress.processed / aiJobProgress.total) * 100) 
      : 0;
    
    return (
      <div className="mb-3 p-3 bg-gradient-to-r from-purple-50 to-blue-50 border border-purple-200 rounded-lg" data-testid="ai-job-progress">
        <div className="flex items-center gap-3">
          <Sparkles className="w-5 h-5 text-purple-600 animate-pulse" />
          <div className="flex-1">
            <div className="flex items-center justify-between mb-1">
              <span className="font-medium text-purple-900 text-sm">AI Analysis in Progress</span>
              <span className="text-sm text-purple-600">{aiJobProgress.processed}/{aiJobProgress.total}</span>
            </div>
            <div className="w-full bg-purple-200 rounded-full h-2">
              <div 
                className="bg-purple-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${progressPercent}%` }}
              />
            </div>
          </div>
          <Loader2 className="w-5 h-5 text-purple-500 animate-spin" />
        </div>
      </div>
    );
  };

  if (error) return null;

  // All resolved - show success
  if (unresolvedAnomalies.length === 0 && resolvedAnomaliesList.length === 0) {
    return (
      <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-lg p-3 mb-4" data-testid="anomaly-banner-clean">
        <div className="flex items-center gap-2">
          <CheckCircle2 className="w-5 h-5 text-green-600" />
          <span className="font-medium text-green-800">No Anomalies Detected</span>
          <span className="text-sm text-green-600">— AI verified data consistency</span>
        </div>
      </div>
    );
  }

  // All anomalies resolved
  if (unresolvedAnomalies.length === 0 && resolvedAnomaliesList.length > 0) {
    return (
      <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-lg p-3 mb-4" data-testid="anomaly-banner-all-resolved">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <CheckCircle2 className="w-5 h-5 text-green-600" />
            <span className="font-medium text-green-800">All Anomalies Reviewed</span>
            <span className="text-sm text-green-600">— {resolvedAnomaliesList.length} resolved</span>
          </div>
          <button
            onClick={() => setShowResolved(!showResolved)}
            className="text-xs text-green-700 hover:text-green-900 underline"
          >
            {showResolved ? 'Hide' : 'View'} resolved
          </button>
        </div>
        {showResolved && (
          <div className="mt-3 space-y-2">
            {resolvedAnomaliesList.map(a => (
              <div key={a.id} className="p-2 bg-green-100/50 rounded text-sm flex items-start justify-between gap-2">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-green-600" />
                    <span className="line-through text-gray-600">{a.title}</span>
                  </div>
                  {a.resolution_note && (
                    <p className="text-xs text-gray-500 mt-1 ml-6 italic">"{a.resolution_note}"</p>
                  )}
                </div>
                <button
                  onClick={() => handleUnresolve(a.id)}
                  className="text-xs text-gray-500 hover:text-red-600"
                  title="Mark as unresolved"
                >
                  Undo
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <>
      {/* Resolve Modal */}
      {resolveModalOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-gray-900">Resolve Anomaly</h3>
              <button onClick={() => setResolveModalOpen(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <p className="text-sm text-gray-600 mb-3">
              Add a note explaining why this anomaly is being dismissed (optional):
            </p>
            <textarea
              value={resolveNote}
              onChange={(e) => setResolveNote(e.target.value)}
              placeholder="e.g., Normal seasonal variance, One-time equipment purchase, Confirmed with client..."
              className="w-full p-3 border border-gray-300 rounded-lg text-sm resize-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              rows={3}
              autoFocus
            />
            <div className="flex justify-end gap-2 mt-4">
              <button
                onClick={() => setResolveModalOpen(false)}
                className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800"
              >
                Cancel
              </button>
              <button
                onClick={confirmResolve}
                className="px-4 py-2 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-1"
              >
                <Check className="w-4 h-4" /> Mark Resolved
              </button>
            </div>
          </div>
        </div>
      )}

      {/* AI Analysis Progress Indicator */}
      {renderAiJobProgress()}

      {/* Main Banner */}
      <div className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-lg mb-4 overflow-hidden" data-testid="anomaly-banner">
        {/* Header */}
        <div className="p-3 flex items-center justify-between cursor-pointer hover:bg-amber-100/50 transition-colors" onClick={() => setExpanded(!expanded)}>
          <div className="flex items-center gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-600" />
            <div>
              <span className="font-semibold text-amber-900">
                {unresolvedAnomalies.length} Anomal{unresolvedAnomalies.length === 1 ? 'y' : 'ies'} Detected
              </span>
              {highPriority.length > 0 && (
                <span className="ml-2 text-sm text-amber-700">({highPriority.length} high priority)</span>
              )}
              {resolvedAnomaliesList.length > 0 && (
                <span className="ml-2 text-sm text-green-600">({resolvedAnomaliesList.length} resolved)</span>
              )}
              {aiAnalyzedCount > 0 && (
                <span className="ml-2 text-sm text-purple-600 flex items-center gap-1">
                  <Sparkles className="w-3 h-3" /> All AI Analyzed
                  {usingCachedAnalysis && <span className="text-purple-400">(cached)</span>}
                </span>
              )}
            </div>
          </div>
          <button className="p-1 hover:bg-amber-200 rounded-lg transition-colors" data-testid="anomaly-toggle">
            {expanded ? <ChevronUp className="w-5 h-5 text-amber-700" /> : <ChevronDown className="w-5 h-5 text-amber-700" />}
          </button>
        </div>

        {/* Expanded List */}
        {expanded && (
          <div className="px-3 pb-3 space-y-2">
            {/* Cached Analysis Info */}
            {usingCachedAnalysis && cachedAnalysis && (
              <div className="p-2 bg-purple-50 border border-purple-200 rounded text-xs text-purple-700 flex items-center gap-2">
                <span>📦</span>
                <span>
                  <strong>Using saved analysis</strong> from {new Date(cachedAnalysis.analyzedAt).toLocaleString()}
                </span>
              </div>
            )}
            
            {/* Adaptive Thresholds Info */}
            {thresholdsUsed && resolvedAnomaliesList.length >= 3 && (
              <div className="p-2 bg-blue-50 border border-blue-200 rounded text-xs text-blue-700 flex items-center gap-2">
                <span>📈</span>
                <span>
                  <strong>Learning from your feedback:</strong> Thresholds adjusted based on {resolvedAnomaliesList.length} resolved items
                  {thresholdsUsed.revenue_change_pct > 0.25 && ` (revenue: ${(thresholdsUsed.revenue_change_pct * 100).toFixed(0)}%)`}
                  {thresholdsUsed.large_transaction_amount > 10000 && ` (large txn: $${thresholdsUsed.large_transaction_amount.toLocaleString()})`}
                </span>
              </div>
            )}
            
            {displayAnomalies.map((anomaly) => {
              const config = severityConfig[anomaly.severity];
              return (
                <div key={anomaly.id} className={`p-3 rounded-lg ${config.bg} ${config.border} border`} data-testid={`anomaly-item-${anomaly.id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`px-2 py-0.5 text-xs font-medium rounded ${config.badge}`}>
                          {anomaly.severity.toUpperCase()}
                        </span>
                        {anomaly.likely_false_positive && (
                          <span className="px-2 py-0.5 text-xs font-medium rounded bg-blue-100 text-blue-700">
                            🤖 Likely OK
                          </span>
                        )}
                        <span className="font-medium text-gray-900 truncate">{anomaly.title}</span>
                      </div>
                      <p className="text-sm text-gray-600 mt-1">{anomaly.description}</p>
                      {anomaly.explanation && (
                        <p className="text-xs text-gray-500 mt-1.5 leading-relaxed">{anomaly.explanation}</p>
                      )}
                      
                      {/* AI Insight */}
                      {anomaly.ai_analysis && (
                        <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded text-xs">
                          <div className="flex items-start gap-1">
                            <span className="text-blue-600">🤖</span>
                            <div>
                              <span className="font-medium text-blue-800">AI Insight:</span>{' '}
                              <span className="text-blue-700">{anomaly.ai_analysis.explanation}</span>
                              {anomaly.ai_recommendation && (
                                <p className="mt-1 text-blue-600 italic">→ {anomaly.ai_recommendation}</p>
                              )}
                            </div>
                          </div>
                        </div>
                      )}
                      
                      {/* Details */}
                      <div className="flex items-center gap-3 mt-2 text-xs text-gray-500 flex-wrap">
                        {anomaly.related_amount && <span>Amount: <strong>${anomaly.related_amount.toLocaleString()}</strong></span>}
                        {anomaly.affected_period && <span>Period: <strong>{anomaly.affected_period}</strong></span>}
                        {anomaly.affected_vendor && <span className="truncate max-w-[150px]">Vendor: <strong>{anomaly.affected_vendor}</strong></span>}
                        {anomaly.deviation_percent && <span>Deviation: <strong>{anomaly.deviation_percent.toFixed(1)}%</strong></span>}
                      </div>
                    </div>
                    
                    {/* Action Buttons */}
                    <div className="flex flex-col gap-1 shrink-0">
                      {onViewTransaction && anomaly.transaction_id && (
                        <button
                          onClick={(e) => { e.stopPropagation(); onViewTransaction(anomaly.transaction_id!); }}
                          className="px-2 py-1 text-xs bg-blue-100 text-blue-700 hover:bg-blue-200 rounded flex items-center gap-1 transition-colors"
                          data-testid={`view-txn-${anomaly.id}`}
                        >
                          View <ExternalLink className="w-3 h-3" />
                        </button>
                      )}
                      {onResolveAnomaly && (
                        <button
                          onClick={(e) => { e.stopPropagation(); handleResolve(anomaly.id); }}
                          className="px-2 py-1 text-xs bg-green-100 text-green-700 hover:bg-green-200 rounded flex items-center gap-1 transition-colors"
                          data-testid={`resolve-${anomaly.id}`}
                        >
                          <Check className="w-3 h-3" /> Resolve
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}

            {/* Show More/Less */}
            {unresolvedAnomalies.length > 5 && (
              <button onClick={(e) => { e.stopPropagation(); setShowAll(!showAll); }} className="w-full py-2 text-sm text-amber-700 hover:text-amber-900 font-medium">
                {showAll ? 'Show Less' : `Show ${unresolvedAnomalies.length - 5} More`}
              </button>
            )}

            {/* Resolved Section */}
            {resolvedAnomaliesList.length > 0 && (
              <div className="pt-2 border-t border-amber-200">
                <button onClick={() => setShowResolved(!showResolved)} className="flex items-center gap-2 text-sm text-green-700 hover:text-green-900">
                  <CheckCircle2 className="w-4 h-4" />
                  {showResolved ? 'Hide' : 'Show'} {resolvedAnomaliesList.length} resolved
                </button>
                {showResolved && (
                  <div className="mt-2 space-y-1">
                    {resolvedAnomaliesList.map(a => (
                      <div key={a.id} className="p-2 bg-green-50 rounded text-sm flex items-start justify-between gap-2">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <Check className="w-4 h-4 text-green-600 shrink-0" />
                            <span className="line-through text-gray-500">{a.title}</span>
                          </div>
                          {a.resolution_note && (
                            <p className="text-xs text-gray-500 mt-1 ml-6 italic flex items-start gap-1">
                              <MessageSquare className="w-3 h-3 mt-0.5 shrink-0" />
                              "{a.resolution_note}"
                            </p>
                          )}
                        </div>
                        <button onClick={() => handleUnresolve(a.id)} className="text-xs text-gray-400 hover:text-red-600 shrink-0" title="Unresolve">
                          Undo
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Info Note */}
            <div className="text-xs text-gray-500 text-center pt-1">
              Review and resolve items before proceeding. Click "Resolve" to dismiss with a note.
            </div>
          </div>
        )}
      </div>
    </>
  );
}

export default AnomalyBanner;
